import { useEffect, useRef, useState } from "react";
import { endpoints, asset } from "../lib/api";
import PageHero from "../components/PageHero";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

function Tile({ item, index, onOpen }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <figure
      data-testid={`gallery-item-${index}`}
      className="group relative aspect-[4/3] rounded-2xl overflow-hidden soft-shadow bg-[#EDE9DF] cursor-zoom-in"
      onClick={() => onOpen(index)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(index); } }}
      aria-label={`Open ${item.title || "photo"} in lightbox`}
    >
      <img
        src={asset(item.image)}
        alt={item.title || "Byer Fountain Motor Inn"}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
      />
      <figcaption className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[#0B192C]/85 via-[#0B192C]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <span className="text-white font-serif text-xl md:text-2xl leading-tight">{item.title}</span>
      </figcaption>
    </figure>
  );
}

function Lightbox({ items, index, onClose, onIndex }) {
  const touch = useRef({ x: 0, y: 0, dx: 0 });
  const item = items[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onIndex((index + 1) % items.length);
      else if (e.key === "ArrowLeft") onIndex((index - 1 + items.length) % items.length);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [index, items.length, onClose, onIndex]);

  const start = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY, dx: 0 }; };
  const move = (e) => { touch.current.dx = e.touches[0].clientX - touch.current.x; };
  const end = () => {
    if (Math.abs(touch.current.dx) > 60) {
      onIndex(touch.current.dx < 0 ? (index + 1) % items.length : (index - 1 + items.length) % items.length);
    }
    touch.current.dx = 0;
  };

  if (!item) return null;

  return (
    <div
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-[2147483000] bg-black/92 backdrop-blur-sm flex items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={start} onTouchMove={move} onTouchEnd={end}
    >
      <button
        onClick={onClose}
        data-testid="lightbox-close"
        aria-label="Close lightbox"
        className="absolute top-5 right-5 w-11 h-11 grid place-items-center rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-[#0B192C] text-white transition-colors z-10"
      ><X size={22} /></button>

      {items.length > 1 && (
        <>
          <button
            onClick={() => onIndex((index - 1 + items.length) % items.length)}
            data-testid="lightbox-prev"
            aria-label="Previous photo"
            className="absolute left-3 md:left-7 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 grid place-items-center rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-[#0B192C] text-white transition-colors z-10"
          ><ChevronLeft size={26} /></button>
          <button
            onClick={() => onIndex((index + 1) % items.length)}
            data-testid="lightbox-next"
            aria-label="Next photo"
            className="absolute right-3 md:right-7 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 grid place-items-center rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-[#0B192C] text-white transition-colors z-10"
          ><ChevronRight size={26} /></button>
        </>
      )}

      <figure className="max-w-[92vw] max-h-[86vh] flex flex-col items-center gap-4">
        <img
          key={item.id}
          src={asset(item.image)}
          alt={item.title || "Byer Fountain Motor Inn"}
          className="max-w-[92vw] max-h-[78vh] object-contain rounded-xl shadow-2xl select-none"
          draggable={false}
        />
        <figcaption className="inline-flex items-center gap-4 self-center bg-[#0B192C]/70 backdrop-blur-md border border-[#D4AF37]/30 rounded-full px-6 py-2.5 shadow-lg">
          <span className="text-white font-serif text-xl md:text-2xl font-semibold tracking-wide leading-none">{item.title}</span>
          <span className="w-px h-4 bg-[#D4AF37]/50" />
          <span className="text-[#D4AF37] font-medium text-sm tracking-widest tabular-nums leading-none">{String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}</span>
        </figcaption>
      </figure>
    </div>
  );
}

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lbIndex, setLbIndex] = useState(-1);
  useEffect(() => {
    endpoints.gallery()
      .then((rows) => setItems(rows || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHero
        overline="Photo Gallery"
        title="A Closer Look"
        subtitle="From golden-hour arrivals to the glow of the fountain at dusk — welcome to Byer Fountain."
        image="/static/generated/byer-night.png"
      />

      <section className="bg-[#F7F5F0] py-24" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[4/3] rounded-2xl bg-[#EDE9DF] animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="text-center font-serif text-[#0B192C]/60 py-16" data-testid="gallery-empty">
              Our gallery is being curated. Please check back soon.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((it, i) => (
                <Tile key={it.id} item={it} index={i} onOpen={setLbIndex} />
              ))}
            </div>
          )}
        </div>
      </section>

      {lbIndex >= 0 && items[lbIndex] && (
        <Lightbox items={items} index={lbIndex} onClose={() => setLbIndex(-1)} onIndex={setLbIndex} />
      )}
    </div>
  );
}
